import { useState, useRef, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  Grid, CircularProgress, Link, InputAdornment, IconButton,
} from '@mui/material';
import { LockReset, Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const ForgotPassword = () => {
  const [step,       setStep]      = useState(1); // 1=email, 2=code+newpwd
  const [email,      setEmail]     = useState('');
  const [codes,      setCodes]     = useState(['', '', '', '', '', '']);
  const [newPwd,     setNewPwd]    = useState('');
  const [confirmPwd, setConfirmPwd]= useState('');
  const [showPwd,    setShowPwd]   = useState(false);
  const [error,      setError]     = useState('');
  const [info,       setInfo]      = useState('');
  const [loading,    setLoading]   = useState(false);
  const inputRefs = useRef([]);
  const navigate  = useNavigate();
  const location  = useLocation();

  // Magic link from the reset email (/forgot-password?email=…&code=…): pre-fill
  // the email and 6-digit code and jump straight to the "set new password" step,
  // so the user doesn't have to copy anything.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const em = params.get('email');
    const cd = (params.get('code') || '').replace(/\D/g, '').slice(0, 6);
    if (em) setEmail(em);
    if (cd.length === 6) { setCodes(cd.split('')); setStep(2); }
  }, [location.search]);

  const handleDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...codes];
    next[i] = val;
    setCodes(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !codes[i] && i > 0)
      inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) { setCodes(paste.split('')); inputRefs.current[5]?.focus(); e.preventDefault(); }
  };

  const code = codes.join('');

  const [emailFailed, setEmailFailed] = useState(false);

  const sendCode = async () => {
    if (!email) { setError('Please enter your email address'); return; }
    setLoading(true); setError(''); setInfo(''); setEmailFailed(false);
    try {
      const res = await axios.post(`${API}/api/auth/forgot-password`, { email });
      if (res.data.emailFailed) {
        setEmailFailed(true);
        setInfo(res.data.message);
      } else {
        setInfo(res.data.message);
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Request failed. Please try again.');
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (code.length < 6)    { setError('Please enter the full 6-digit code'); return; }
    if (!newPwd)             { setError('New password is required'); return; }
    if (newPwd.length < 6)  { setError('Password must be at least 6 characters'); return; }
    if (newPwd !== confirmPwd) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/api/auth/reset-password`, { email, code, newPassword: newPwd });
      navigate('/login', { state: { info: 'Password reset successfully. You can now sign in.' } });
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6f8' }}>
      <Box sx={{ width: '100%', maxWidth: 420, px: 2 }}>
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <LockReset color="warning" />
              <Typography variant="h6" fontWeight={700}>
                {step === 1 ? 'Forgot your password?' : 'Enter reset code'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {step === 1
                ? 'Enter your email and we\'ll send a 6-digit reset code.'
                : `Enter the code sent to ${email} and choose a new password.`}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {info && !emailFailed && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
            {emailFailed && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Email delivery is slow or unavailable.</strong>
                <br />
                The code may still arrive in a moment — check your inbox (and spam), then{' '}
                <Link component="button" onClick={() => { setEmailFailed(false); setInfo(''); setStep(2); }}>
                  enter the code here
                </Link>.
                <br /><br />
                If no code arrives, ask your <strong>school administrator</strong> to reset your password from the
                Users management page, or contact <strong>edumis@dataportech.com</strong> if you have no admin.
              </Alert>
            )}

            {step === 1 ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Email Address" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendCode()}
                    fullWidth size="small" autoFocus />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" fullWidth size="large" onClick={sendCode} disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                    {loading ? 'Sending…' : 'Send Reset Code'}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                {/* 6-box OTP */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
                    6-digit code from your email
                  </Typography>
                  <Box display="flex" gap={1} onPaste={handlePaste}>
                    {codes.map((digit, i) => (
                      <TextField key={i} value={digit}
                        onChange={e => handleDigit(i, e.target.value)}
                        onKeyDown={e => handleKeyDown(i, e)}
                        inputRef={el => (inputRefs.current[i] = el)}
                        inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: 24, fontWeight: 900, padding: '10px 0', width: 36 } }}
                        sx={{ width: 46 }}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="New Password" type={showPwd ? 'text' : 'password'}
                    value={newPwd} onChange={e => setNewPwd(e.target.value)}
                    fullWidth size="small" helperText="Minimum 6 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPwd(v => !v)} edge="end">
                            {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Confirm New Password" type={showPwd ? 'text' : 'password'}
                    value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                    fullWidth size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" fullWidth size="large" onClick={resetPassword} disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                    {loading ? 'Resetting…' : 'Reset Password'}
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button variant="text" fullWidth size="small" onClick={sendCode} disabled={loading}>
                    Resend Code
                  </Button>
                </Grid>
              </Grid>
            )}

            <Typography variant="body2" color="text.secondary" align="center" mt={2.5}>
              <Link component="button" onClick={() => navigate('/login')}>Back to Sign In</Link>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
